import { ObjectTypeEnum } from "@/types/refineRealEstate";

export interface NumericValidationLimits {
  readonly min: number;
  readonly max?: number;
}

export interface NumericValidationRange extends NumericValidationLimits {
  readonly max: number;
}

const currentYear = new Date().getFullYear();

const livingAreaMaximumByObjectType: Partial<Record<ObjectTypeEnum, number>> = {
  [ObjectTypeEnum.apartment]: 500,
  [ObjectTypeEnum.multiFamilyHouse]: 2000,
  [ObjectTypeEnum.mixedUseProperty]: 2000,
};

const unitsNumberByObjectType: Partial<Record<ObjectTypeEnum, NumericValidationLimits>> = {
  [ObjectTypeEnum.apartment]: { min: 1 },
  [ObjectTypeEnum.multiFamilyHouse]: { min: 2, max: 998 },
  [ObjectTypeEnum.mixedUseProperty]: { min: 2, max: 998 },
};

export const REAL_ESTATE_VALIDATION_LIMITS = {
  remainingDuration: { minExclusive: 15 },
  actualGroundRent: { min: 0 },
  nonCompensableBuildingPortion: { min: 0, max: 100 },
  plotArea: { min: 1, max: 5000 },
  constructionYear: { min: 1800, max: currentYear + 1 },
  livingArea: {
    min: 1,
    defaultMax: 1000,
    maxByObjectType: livingAreaMaximumByObjectType,
  },
  grossFloorArea: { min: 0, max: 1000 },
  rooms: { min: 0, max: 100 },
  floorNumber: { min: -1, max: 100 },
  numberOfFloors: { min: 1, max: 1000 },
  unitsNumber: { byObjectType: unitsNumberByObjectType },
  garages: { range: { min: 0, max: 50 } },
  energy: { min: 0, max: 10000 },
  photovoltaicConstructionYear: {
    min: 1990,
    formMax: currentYear,
    preValidationMax: currentYear + 1,
  },
  photovoltaicAnnualPowerYield: { min: 0, max: 5000 },
  refurbishmentYear: { min: 1800, max: currentYear },
  outdoorParkingSpaces: { min: 0, max: 100 },
} as const;

export const getLivingAreaLimits = (objectType?: ObjectTypeEnum | null): NumericValidationRange => ({
  min: REAL_ESTATE_VALIDATION_LIMITS.livingArea.min,
  max:
    (objectType && REAL_ESTATE_VALIDATION_LIMITS.livingArea.maxByObjectType[objectType]) ??
    REAL_ESTATE_VALIDATION_LIMITS.livingArea.defaultMax,
});

export const getUnitsNumberLimits = (objectType?: ObjectTypeEnum | null): NumericValidationLimits | undefined =>
  objectType ? REAL_ESTATE_VALIDATION_LIMITS.unitsNumber.byObjectType[objectType] : undefined;

export const getGarageLimits = (): NumericValidationRange => REAL_ESTATE_VALIDATION_LIMITS.garages.range;
